import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchTaskDetail } from '../store/slices/tasksSlice';
import { tasksAPI } from '../services/api';
import { Loading } from '../components/common';
import Modal from '../components/common/Modal';
import { formatDate, formatDateTime, formatFileSize } from '../utils/formatters';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  PaperClipIcon,
  ChatBubbleLeftIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentArrowUpIcon,
  UserIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';

const TaskDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentTask: task, loading } = useSelector((state) => state.tasks);
  const { user } = useSelector((state) => state.auth);
  
  const [responseNote, setResponseNote] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [kpiScore, setKpiScore] = useState(10);
  const [submitting, setSubmitting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [uploadFile, setUploadFile] = useState(null);

  useEffect(() => {
    dispatch(fetchTaskDetail(id));
  }, [dispatch, id]);

  const refreshTask = () => dispatch(fetchTaskDetail(id));

  const handleMarkViewed = async (assignmentId) => {
    try {
      await tasksAPI.viewAssignment(assignmentId);
      refreshTask();
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (assignmentId) => {
    setSubmitting(true);
    try {
      if (uploadFile) {
        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('attachment_type', 'response');
        formData.append('assignment', assignmentId);
        await tasksAPI.uploadAttachment(id, formData);
      }
      await tasksAPI.submitAssignment(assignmentId, { response_note: responseNote });
      toast.success('Javob yuborildi!');
      setResponseNote('');
      setUploadFile(null);
      refreshTask();
    } catch (error) {
      toast.error('Xatolik yuz berdi');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (assignmentId) => {
    try {
      await tasksAPI.reviewAssignment(assignmentId, { action: 'approve', kpi_score: kpiScore });
      toast.success('Qabul qilindi!');
      refreshTask();
    } catch (error) {
      toast.error('Xatolik yuz berdi');
    }
  };

  const handleReject = async () => {
    if (!selectedAssignment) return;
    try {
      await tasksAPI.reviewAssignment(selectedAssignment, { action: 'reject', rejection_reason: rejectionReason });
      toast.success('Qaytarildi!');
      setShowRejectModal(false);
      setRejectionReason('');
      setSelectedAssignment(null);
      refreshTask();
    } catch (error) {
      toast.error('Xatolik yuz berdi');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await tasksAPI.addComment(id, { text: newComment, task: id });
      toast.success('Izoh qo\'shildi');
      setNewComment('');
      refreshTask();
    } catch (error) {
      toast.error('Xatolik yuz berdi');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'badge-info', viewed: 'badge-info', in_progress: 'badge-warning', submitted: 'badge-warning',
      approved: 'badge-success', rejected: 'badge-danger', not_completed: 'badge-danger',
    };
    return styles[status] || 'badge-gray';
  };

  if (loading || !task) return <Loading />;

  const myAssignment = task.assignments?.find(a => a.organization === user?.organization);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button onClick={() => navigate('/tasks')} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-800">{task.title}</h1>
            <span className={`badge ${task.is_overdue ? 'badge-danger' : getStatusBadge(task.status)}`}>
              {task.is_overdue ? "Muddati o'tgan" : task.status_display}
            </span>
          </div>
          <p className="text-gray-500 mt-1">{task.category?.name}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="card">
            <h2 className="font-semibold text-gray-800 mb-3">Tavsif</h2>
            <p className="text-gray-600 whitespace-pre-wrap">{task.description}</p>
          </div>

          {/* Attachments */}
          {task.attachments?.length > 0 && (
            <div className="card">
              <h2 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <PaperClipIcon className="w-5 h-5" />
                Fayllar ({task.attachments.length})
              </h2>
              <div className="space-y-2">
                {task.attachments.map((file) => (
                  <a key={file.id} href={file.file} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                    <DocumentArrowUpIcon className="w-8 h-8 text-blue-500" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 truncate">{file.file_name}</p>
                      <p className="text-xs text-gray-500">{formatFileSize(file.file_size)}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Assignments */}
          <div className="card">
            <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <BuildingOfficeIcon className="w-5 h-5" />
              Tashkilotlar bo'yicha ijro ({task.assignments?.length || 0})
            </h2>
            <div className="space-y-4">
              {task.assignments?.map((assignment) => (
                <div key={assignment.id} className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-medium text-gray-800">{assignment.organization_short_name || assignment.organization_name}</p>
                      {assignment.assigned_to_name && (
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <UserIcon className="w-4 h-4" /> {assignment.assigned_to_name}
                        </p>
                      )}
                    </div>
                    <span className={`badge ${getStatusBadge(assignment.status)}`}>{assignment.status_display}</span>
                  </div>

                  {assignment.response_note && (
                    <div className="mt-3 p-3 bg-white rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Izoh:</p>
                      <p className="text-gray-700">{assignment.response_note}</p>
                    </div>
                  )}

                  {assignment.rejection_reason && (
                    <div className="mt-3 p-3 bg-red-50 rounded-lg">
                      <p className="text-sm text-red-600 mb-1">Qaytarish sababi:</p>
                      <p className="text-red-700">{assignment.rejection_reason}</p>
                    </div>
                  )}

                  {/* OTM Auditor actions */}
                  {user?.role === 'otm_auditor' && user?.organization === assignment.organization && (
                    <>
                      {assignment.status === 'new' && (
                        <button onClick={() => handleMarkViewed(assignment.id)} className="mt-3 btn-outline btn-sm">
                          Tanishdim
                        </button>
                      )}
                      {['viewed', 'in_progress', 'rejected'].includes(assignment.status) && (
                        <div className="mt-4 pt-4 border-t space-y-3">
                          <textarea
                            className="textarea"
                            placeholder="Izoh yozing..."
                            value={responseNote}
                            onChange={(e) => setResponseNote(e.target.value)}
                          />
                          <div>
                            <label className="label">Fayl yuklash</label>
                            <input type="file" onChange={(e) => setUploadFile(e.target.files[0])}
                              accept=".pdf,.docx,.xlsx" className="input" />
                          </div>
                          <button onClick={() => handleSubmit(assignment.id)} disabled={submitting} className="btn-primary">
                            {submitting ? 'Yuborilmoqda...' : 'Javob yuborish'}
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {/* Admin actions */}
                  {user?.role === 'admin' && assignment.status === 'submitted' && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-center gap-4 mb-3">
                        <label className="text-sm text-gray-600">KPI ball:</label>
                        <input type="number" min="0" max="100" value={kpiScore}
                          onChange={(e) => setKpiScore(parseInt(e.target.value))}
                          className="input w-24" />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => handleApprove(assignment.id)} className="btn-success">
                          <CheckCircleIcon className="w-5 h-5" /> Qabul qilish
                        </button>
                        <button onClick={() => { setSelectedAssignment(assignment.id); setShowRejectModal(true); }} className="btn-danger">
                          <XCircleIcon className="w-5 h-5" /> Qaytarish
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Comments */}
          <div className="card">
            <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <ChatBubbleLeftIcon className="w-5 h-5" />
              Izohlar ({task.comments?.length || 0})
            </h2>
            <div className="space-y-4 mb-4">
              {task.comments?.map((comment) => (
                <div key={comment.id} className="flex gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                    {comment.user_name?.[0]}
                  </div>
                  <div className="flex-1 bg-gray-50 rounded-lg p-3">
                    <div className="flex justify-between items-start mb-1">
                      <p className="font-medium text-gray-800">{comment.user_name}</p>
                      <p className="text-xs text-gray-400">{formatDateTime(comment.created_at)}</p>
                    </div>
                    <p className="text-gray-600">{comment.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input type="text" className="input" placeholder="Izoh yozing..."
                value={newComment} onChange={(e) => setNewComment(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddComment()} />
              <button onClick={handleAddComment} className="btn-primary">Yuborish</button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="card">
            <h3 className="font-semibold text-gray-800 mb-4">Ma'lumotlar</h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-500">Muddat</span>
                <span className={`font-medium ${task.is_overdue ? 'text-red-600' : ''}`}>{formatDateTime(task.deadline)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Muhimlik</span>
                <span className="font-medium">{task.priority_display}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Turi</span>
                <span className="font-medium">{task.task_type_display}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Yaratuvchi</span>
                <span className="font-medium">{task.created_by_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Yaratilgan</span>
                <span className="font-medium">{formatDate(task.created_at)}</span>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="font-semibold text-gray-800 mb-4">Ijro statistikasi</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Jami</span>
                <span className="font-bold text-lg">{task.assignments?.length || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-green-600">Bajarilgan</span>
                <span className="font-bold text-lg text-green-600">
                  {task.assignments?.filter(a => a.status === 'approved').length || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-yellow-600">Kutilmoqda</span>
                <span className="font-bold text-lg text-yellow-600">
                  {task.assignments?.filter(a => a.status === 'submitted').length || 0}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div className="bg-green-600 h-2 rounded-full" style={{ 
                  width: `${(task.assignments?.filter(a => a.status === 'approved').length / task.assignments?.length) * 100 || 0}%` 
                }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      <Modal isOpen={showRejectModal} onClose={() => setShowRejectModal(false)} title="Qaytarish sababi" size="md">
        <div className="space-y-4">
          <textarea className="textarea" placeholder="Qaytarish sababini yozing..."
            value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} />
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowRejectModal(false)} className="btn-secondary">Bekor qilish</button>
            <button onClick={handleReject} className="btn-danger">Qaytarish</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TaskDetail;
